/**
 * Synthesised sound. No audio files — every cue is generated from oscillators, so the game stays
 * offline-safe and adds nothing to the bundle.
 *
 * Sound is most of "game feel". A jump that clicks and a death that thuds read as responsive even
 * when the visuals are simple; silence reads as a prototype.
 *
 * Browsers block audio until the user interacts, so `unlock()` runs on the first input.
 */

let ctx: AudioContext | null = null;
let muted = false;

function context(): AudioContext | null {
	if (muted) return null;
	if (ctx) return ctx;
	try {
		const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
		if (!Ctor) return null;
		ctx = new Ctor();
	} catch {
		return null;
	}
	return ctx;
}

/** Call from the first user gesture — before that, browsers keep the context suspended. */
export function unlockAudio(): void {
	const c = context();
	if (c && c.state === 'suspended') void c.resume().catch(() => {});
}

export function setMuted(next: boolean): void {
	muted = next;
	if (muted && ctx) void ctx.suspend().catch(() => {});
	else if (!muted && ctx) void ctx.resume().catch(() => {});
}

export function isMuted(): boolean {
	return muted;
}

interface ToneOptions {
	freq: number;
	/** Slide to this frequency over the note — an upward slide reads as "up", downward as failure. */
	toFreq?: number;
	duration: number;
	type?: OscillatorType;
	gain?: number;
	delay?: number;
}

function tone({ freq, toFreq, duration, type = 'square', gain = 0.08, delay = 0 }: ToneOptions): void {
	const c = context();
	if (!c) return;
	const start = c.currentTime + delay;

	const osc = c.createOscillator();
	const amp = c.createGain();
	osc.type = type;
	osc.frequency.setValueAtTime(freq, start);
	if (toFreq !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, toFreq), start + duration);

	// A short attack and an exponential tail — a raw gate click sounds broken.
	amp.gain.setValueAtTime(0.0001, start);
	amp.gain.exponentialRampToValueAtTime(gain, start + 0.012);
	amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);

	osc.connect(amp);
	amp.connect(c.destination);
	osc.start(start);
	osc.stop(start + duration + 0.02);
}

function noise(duration: number, gain = 0.12): void {
	const c = context();
	if (!c) return;
	const frames = Math.floor(c.sampleRate * duration);
	const buffer = c.createBuffer(1, frames, c.sampleRate);
	const data = buffer.getChannelData(0);
	for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);

	const src = c.createBufferSource();
	const amp = c.createGain();
	src.buffer = buffer;
	amp.gain.value = gain;
	src.connect(amp);
	amp.connect(c.destination);
	src.start();
}

export const sfx = {
	jump: () => tone({ freq: 320, toFreq: 620, duration: 0.12, type: 'square', gain: 0.05 }),
	land: () => noise(0.06, 0.05),
	score: () => tone({ freq: 880, toFreq: 1320, duration: 0.09, type: 'triangle', gain: 0.045 }),
	/** Near miss: a quick two-note lift, so nerve is audibly rewarded. */
	nearMiss: () => {
		tone({ freq: 660, duration: 0.06, type: 'triangle', gain: 0.05 });
		tone({ freq: 990, duration: 0.09, type: 'triangle', gain: 0.05, delay: 0.06 });
	},
	death: () => {
		tone({ freq: 220, toFreq: 60, duration: 0.5, type: 'sawtooth', gain: 0.1 });
		noise(0.25, 0.1);
	},
};
